import Box from '@mui/material/Box';
import TableContainer from '@mui/material/TableContainer';
import { TableBody, TableHead, TableRow, styled } from '@mui/material';
import Typography from '@mui/material/Typography';
import { StyledTable, TableCell } from 'further-ui/components/Table';
import { CurrencyCode } from 'further-types/lp/vehicle';
import buildPcapTableRows from './build-pcap-table-rows';
import PcapDataRow from './PcapDataRow';
import PeriodHeaderCell from './PeriodHeaderCell';
import type { PcapResult } from './types';

const RelativeTableContainer = styled(TableContainer)(() => ({
  position: 'relative',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  overflowX: 'auto',
}));

const PcapStyledTable = styled(StyledTable)(() => ({
  width: '100%',
  minWidth: 'max-content',
}));

const BorderlessHeaderCell = styled(TableCell)(() => ({
  borderTop: 'none',
  borderBottom: 'none',
  borderLeft: 'none',
  borderRight: 'none',
}));

const HeaderBottomBorderCell = styled(BorderlessHeaderCell)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
}));

const GroupHeaderCell = styled(BorderlessHeaderCell)(() => ({
  textAlign: 'left',
}));

const StatementTextWithVehicle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
}));

const HeaderLeadCellContent = styled(Box)(() => ({}));

type Props = {
  pcap: PcapResult;
  currency: CurrencyCode;
  investorName: string;
  vehicleName?: string;
};

const PcapTable: React.FC<Props> = ({
  pcap,
  currency,
  investorName,
  vehicleName,
}) => {
  const rows = buildPcapTableRows(pcap.lp, pcap.fund);

  return (
    <RelativeTableContainer>
      <PcapStyledTable borderless={false}>
        <TableHead>
          <TableRow>
            <HeaderBottomBorderCell isHeaderCell size="small" rowSpan={2}>
              <HeaderLeadCellContent>
                {vehicleName ? (
                  <>
                    <Typography variant="h6">{vehicleName}</Typography>
                    <StatementTextWithVehicle variant="h6" color="grey.500">
                      Capital account statement for {investorName}.
                    </StatementTextWithVehicle>
                  </>
                ) : (
                  <Typography variant="h6" color="grey.500">
                    Capital account statement for {investorName}.
                  </Typography>
                )}
              </HeaderLeadCellContent>
            </HeaderBottomBorderCell>
            <GroupHeaderCell isHeaderCell size="small" colSpan={3}>
              <Typography variant="h6">Limited partner</Typography>
            </GroupHeaderCell>

            <GroupHeaderCell isHeaderCell size="small" colSpan={3}>
              <Typography variant="h6">Fund</Typography>
            </GroupHeaderCell>
          </TableRow>
          <TableRow>
            <PeriodHeaderCell title="Quarter-to-date" period={pcap.lp.qtd} />
            <PeriodHeaderCell title="Year-to-date" period={pcap.lp.ytd} />
            <PeriodHeaderCell
              title="Since inception"
              period={pcap.lp.sinceInception}
            />
            <PeriodHeaderCell title="Quarter-to-date" period={pcap.fund.qtd} />
            <PeriodHeaderCell title="Year-to-date" period={pcap.fund.ytd} />
            <PeriodHeaderCell
              title="Since inception"
              period={pcap.fund.sinceInception}
            />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <PcapDataRow
              key={`${row.label}-${i}`}
              row={row}
              currency={currency}
            />
          ))}
        </TableBody>
      </PcapStyledTable>
    </RelativeTableContainer>
  );
};

export default PcapTable;
